"use client";

import Link from "next/link";
import {
  BUSINESS_INFO,
  formatBusinessNumber,
} from "@/lib/config/business";
import {
  SUPPORT_EMAIL,
  SUPPORT_HOURS_LABEL,
  SUPPORT_KAKAO_HANDLE,
  SUPPORT_KAKAO_URL,
  SUPPORT_PLATFORM_NOTICE,
} from "@/lib/config/customerSupport";

function FooterColumn({
  title,
  links,
}: {
  title: string;
  links: Array<{ href: string; label: string; external?: boolean }>;
}) {
  return (
    <div>
      <h3 className="mb-3 text-sm font-bold text-white">{title}</h3>
      <ul className="space-y-2">
        {links.map(link => (
          <li key={link.href + link.label}>
            {link.external ? (
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-gray-400 transition-colors hover:text-white"
              >
                {link.label}
              </a>
            ) : (
              <Link
                href={link.href}
                className="text-xs text-gray-400 transition-colors hover:text-white"
              >
                {link.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  const businessNumber = BUSINESS_INFO.businessNumber
    ? formatBusinessNumber(BUSINESS_INFO.businessNumber)
    : null;

  return (
    <footer className="mt-auto border-t border-gray-800 bg-gray-950 text-gray-300 pb-mobile-nav md:pb-8">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="grid gap-8 lg:grid-cols-[1.2fr_2fr]">
          {/* Left: support CTAs like Kmong */}
          <div>
            <div className="flex flex-wrap gap-2">
              <Link
                href="/contact"
                className="rounded-lg border border-gray-600 bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:border-gray-400"
              >
                1:1 문의
              </Link>
              <Link
                href="/contact?audience=pro"
                className="rounded-lg border border-gray-600 bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:border-gray-400"
              >
                프로 지원
              </Link>
            </div>
            <p className="mt-3 text-xs text-gray-500">{SUPPORT_HOURS_LABEL}</p>
            <p className="mt-2 max-w-sm text-[11px] leading-relaxed text-gray-500">
              {SUPPORT_PLATFORM_NOTICE}
            </p>
            <div className="mt-4 space-y-1 text-xs text-gray-400">
              <p>
                이메일{" "}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-gray-300 hover:text-white"
                >
                  {SUPPORT_EMAIL}
                </a>
              </p>
              <p className="text-[11px] text-gray-500">
                카카오톡{" "}
                <a
                  href={SUPPORT_KAKAO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 underline-offset-2 hover:text-gray-300 hover:underline"
                >
                  {SUPPORT_KAKAO_HANDLE}
                </a>
              </p>
            </div>
            <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500">
              <Link href="/terms" className="hover:text-gray-300">
                이용약관
              </Link>
              <Link href="/privacy" className="font-semibold text-gray-400 hover:text-white">
                개인정보취급방침
              </Link>
            </div>
          </div>

          {/* Right: columns */}
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
            <FooterColumn
              title="커넥톤 소개"
              links={[
                { href: "/about", label: "서비스 소개" },
                { href: "/lessons", label: "레슨" },
                { href: "/expert-feedback", label: "프로 피드백" },
              ]}
            />
            <FooterColumn
              title="이용 안내"
              links={[
                { href: "/music-analysis/analyze", label: "AI 음악분석" },
                { href: "/profile/expert-analysis", label: "피드백 신청내역" },
                { href: "/profile/points", label: "포인트" },
              ]}
            />
            <FooterColumn
              title="프로 안내"
              links={[
                { href: "/instructor/register", label: "프로로 시작하기" },
                { href: "/contact?audience=pro", label: "프로 지원 문의" },
              ]}
            />
            <FooterColumn
              title="고객지원"
              links={[
                { href: "/contact", label: "1:1 문의" },
                { href: "/profile/inquiries", label: "내 문의함" },
                { href: "/faq", label: "자주 묻는 질문" },
              ]}
            />
          </div>
        </div>

        {/* Business info */}
        <div className="mt-8 border-t border-gray-800 pt-4">
          <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[10px] leading-relaxed text-gray-500 sm:text-[11px]">
            <span>
              <span className="text-gray-400">상호</span> {BUSINESS_INFO.companyName}
            </span>
            {businessNumber && (
              <span>
                <span className="text-gray-400">사업자등록번호</span> {businessNumber}
              </span>
            )}
            {BUSINESS_INFO.ceoName && (
              <span>
                <span className="text-gray-400">대표</span> {BUSINESS_INFO.ceoName}
              </span>
            )}
            {BUSINESS_INFO.address && (
              <span>
                <span className="text-gray-400">주소</span> {BUSINESS_INFO.address}
              </span>
            )}
            {BUSINESS_INFO.mailOrderNumber && (
              <span>
                <span className="text-gray-400">통신판매업신고</span>{" "}
                {BUSINESS_INFO.mailOrderNumber}
              </span>
            )}
            {BUSINESS_INFO.phone && (
              <span className="text-[9px] text-gray-600">
                <span className="text-gray-500">유선</span> {BUSINESS_INFO.phone}
              </span>
            )}
          </p>
          <p className="mt-2 text-[11px] text-gray-600">© 2025 커넥톤</p>
        </div>
      </div>
    </footer>
  );
}
